<template>
  <div class="gs-proof-step">
    <section class="gs-wizard-card gs-proof-step__summary">
      <div class="gs-proof-step__head">
        <h2>Proof widgets</h2>
        <gs-button type="button" variant="primary" size="icon" aria-label="Create proof widget" title="Create proof widget" @click="createWidget">
          <gs-icon name="plus" size="18"></gs-icon>
        </gs-button>
      </div>
      <p>{{ $t('frontend_product_step10_config_proof_widget') }}.</p>
      <div v-if="proofWidgets.length" class="gs-proof-step__list">
        <div
          v-for="proofWidget in proofWidgets"
          :key="proofWidget.id"
          class="gs-proof-step__row"
        >
          <strong>{{ proofWidget.name }}</strong>
          <span>{{ proofWidget.notification_text || $t('frontend_notification_text') }}</span>
          <div>
            <gs-button type="button" variant="primary" size="icon" aria-label="Edit proof widget" title="Edit proof widget" @click="openEditWidget(proofWidget.id)">
              <gs-icon name="edit" size="16"></gs-icon>
            </gs-button>
            <gs-button type="button" variant="ghost" size="icon" class="is-destructive" aria-label="Delete proof widget" title="Delete proof widget" @click="openConfirmModal(proofWidget.id)">
              <gs-icon name="trash" size="16"></gs-icon>
            </gs-button>
          </div>
        </div>
      </div>
      <div v-else class="gs-proof-step__empty">
        {{ $t('frontend_product_step10_dont_have_any_proof') }}
      </div>
    </section>

    <gs-drawer
      v-model="showWidgetEditor"
      size="xl"
      :title="isEditing ? 'Edit proof widget' : $t('frontend_groovesell_create_new_proff_widget')"
      subtitle="Configure the message, display timing, and design for this product's proof widget."
      @close="closeWidgetEditor"
    >
    <aside class="gs-proof-step__drawer">
      <div class="gs-proof-step__drawer-body">
        <div class="flex flex-row mb-2 -mx-4 px-4">
          <gd-tabs
            :tabs="widgetsTabs"
            :current-tab="widgetsTab"
            :wrapper-class="'pt-4 px-0 gs-default-tabs'"
            :tab-class="'pb-4 mr-4 gs-default-tabs-item'"
            :tab-active-class="'border-primary border-b-2 text-primary gs-default-tabs-item-active'"
            :line-class="'gs-default-tabs-active-line'"
            @onClick="handleWidgetsTabChange"
          />
        </div>
        <div class="mt-4">
          <div v-if="widgetsTab == 'info'">
            <div class="-mt-4">
              <div class="flex flex-col w-full mt-4">
                <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_name') }}</label>
                <text-input
                  v-model="proofModel.name"
                  :validator="$v.proofModel.name"
                  :attribute="$t('frontend_name')"
                  value="proofModel.name"
                  :placeholder="$t('frontend_name')"
                />
              </div>
              <div class="flex flex-col w-full mt-4">
                <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_notification_text') }}</label>
                <text-input
                  v-model="proofModel.notification_text"
                  :validator="$v.proofModel.notification_text"
                  :attribute="$t('frontend_notification_text')"
                  :placeholder="$t('frontend_notification_text')"
                />
              </div>
            </div>
          </div>
          <div v-else-if="widgetsTab == 'display'">
            <div class="-mt-4">
              <div class="flex flex-row w-full">
                <div class="w-1/2 flex flex-col w-full mt-4 mr-4">
                  <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_product_step10_delay_between_notification') }}</label>
                  <gd-dropdown
                    v-model="proofModel.delay"
                    size="full"
                    max-height="124px"
                    :placeholder="$t('frontend_delay')"
                    :options="proofOptions"
                  />
                </div>

                <div class="w-1/2 flex flex-col w-full mt-4">
                  <label class="font-medium text-sm text-grey-700 mb-2 text-left"
                    >{{ $t('frontend_product_step10_notification_time_to_show') }})</label
                  >
                  <gd-dropdown
                    v-model="proofModel.duration"
                    size="full"
                    max-height="124px"
                    :placeholder="$t('frontend_duration')"
                    :options="proofDurationOptions"
                  />
                </div>
              </div>

              <div class="flex flex-row w-full">
                <div class="flex flex-col w-1/2 mt-4 mr-4">
                  <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_product_step10_n_contacts_to_display') }}</label>
                  <gd-dropdown
                    v-model="proofModel.no_of_conversions"
                    size="full"
                    max-height="124px"
                    :placeholder="$t('frontend_product_step10_n_contacts_to_display')"
                    :options="proofNoOfNotificationsOptions"
                  />
                </div>
                <div class="flex flex-col w-1/2 mt-4">
                  <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_position') }}</label>
                  <gd-dropdown
                    v-model="proofModel.position"
                    size="full"
                    max-height="124px"
                    :placeholder="$t('frontend_position')"
                    :options="positionOptions"
                  />
                </div>
              </div>

              <div class="flex flex-row w-full">
                <div class="w-1/2 flex flex-col w-full mt-4 mr-4">
                  <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_display_to_mobile') }}</label>
                  <gd-toggle :aria-label="$t('frontend_display_to_mobile')" v-model="proofModel.show_on_mobile" :value="proofModel.show_on_mobile" />
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="widgetsTab == 'design'">
            <div class="-mt-4">
              <div class="flex flex-row w-full">
                <div class="flex flex-col w-1/3 mt-4">
                  <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_title_color') }}</label>
                  <v-swatches
                    v-model="proofModel.title_color"
                    show-fallback
                    fallback-input-type="color"
                    popover-x="right"
                    popover-y="bottom"
                    :trigger-style="swatchTriggerStyle"
                    swatches="text-basic"
                    swatch-size="24"
                  ></v-swatches>
                </div>
                <div class="flex flex-col w-1/3 mt-4">
                  <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_description_color') }}</label>
                  <v-swatches
                    v-model="proofModel.description_color"
                    show-fallback
                    fallback-input-type="color"
                    popover-x="right"
                    popover-y="bottom"
                    :trigger-style="swatchTriggerStyle"
                    swatches="text-basic"
                    swatch-size="24"
                  ></v-swatches>
                </div>
                <div class="flex flex-col w-1/3 mt-4">
                  <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_bg_color') }}</label>
                  <v-swatches
                    v-model="proofModel.background_color"
                    show-fallback
                    fallback-input-type="color"
                    popover-x="right"
                    popover-y="bottom"
                    :trigger-style="swatchTriggerStyle"
                    swatches="text-basic"
                    swatch-size="24"
                  ></v-swatches>
                </div>
              </div>
              <div class="flex flex-row w-full">
                <div class="w-1/2 flex flex-col w-full mt-4 mr-4">
                  <label class="font-medium text-sm text-grey-700 mb-2 text-left">
                    {{ $t('frontend_location_format') }}
                  </label>
                  <gd-dropdown
                    v-model="proofModel.location_format"
                    size="full"
                    max-height="124px"
                    :placeholder="$t('frontend_location_format')"
                    :options="locationFormatOptions"
                  />
                </div>
                <div class="w-1/2 flex flex-col w-full mt-4 mr-4">
                  <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_avatar_display') }}</label>
                  <gd-dropdown
                    v-model="proofModel.avatar_display"
                    size="full"
                    max-height="124px"
                    :placeholder="$t('frontend_avatar_display')"
                    :options="avatarDisplayOptions"
                  />
                </div>
              </div>
              <div class="flex flex-row w-full">
                <div class="w-1/2 flex flex-col w-full mt-4 mr-4">
                  <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_product_step10_show_timestamps') }}</label>
                  <gd-toggle :aria-label="$t('frontend_product_step10_show_timestamps')" v-model="proofModel.show_timestamps" :value="proofModel.show_timestamps" />
                </div>
                <div class="w-1/2 flex flex-col w-full mt-4 mr-4">
                  <label class="font-medium text-sm text-grey-700 mb-2 text-left">{{ $t('frontend_product_step10_show_first_name') }}</label>
                  <gd-toggle :aria-label="$t('frontend_product_step10_show_first_name')" v-model="proofModel.show_real_names" :value="proofModel.show_real_names" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-row w-full mb-6 justify-end">
          <gs-button class="mt-4" variant="primary" @click="saveWidget()">{{ isEditing ? $t('frontend_update') : $t('frontend_create') }}</gs-button>
        </div>
      </div>
    </aside>
    </gs-drawer>
    <gs-alert-dialog
      v-model="showDeleteWidgetConfirm"
      title="Delete proof widget?"
      :copy="$t('frontend_non_revertable')"
      confirm-label="Delete"
      cancel-label="Cancel"
      @confirm="deleteWidget"
    />
  </div>
</template>

<script>
import ProofWidget from '@/models/ProofWidget';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import PencilIcon from '@/assets/images/icons/PencilIcon.svg';
import PlusIcon from '@/assets/images/icons/PlusIcon.svg';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import { Chrome, Photoshop } from 'vue-color';
import ColorPicker from '@groovepages/gd-ui-kit/components/ColorPicker';
import VSwatches from 'vue-swatches';
// Import the styles too, globally
import 'vue-swatches/dist/vue-swatches.css';

export default {
  name: 'Step10',
  components: {
    'chrome-picker': Chrome,
    ColorPicker,
    VSwatches,
    TrashIcon,
    PencilIcon,
    PlusIcon,
  },
  props: {
    model: {
      required: true
    },
    product: {
      required: true
    }
  },
  data() {
    return {
      widgetId: null,
      localModel: this.model,
      title_colors: {
        hex: '#000000',
        source: 'hex'
      },
      description_colors: {
        hex: '#000000',
        source: 'hex'
      },
      backgroud_colors: {
        hex: '#000000',
        source: 'hex'
      },
      swatchTriggerStyle: {
        border: '1px solid #dcdcea'
      },
      isEditing: false,
      showWidgetEditor: false,
      showDeleteWidgetConfirm: false,
      updateValue: '',
      hex: '',
      isOpen: false,
      description_color_isOpen: false,
      backgroud_color_isOpen: false,
      proofWidget: null,
      proofWidgets: [],
      proofModel: {
        product_id: this.$route.params.product_id || this.product.id,
        name: '',
        notification_text: 'Just Purchased ' + this.$store.state.brandData.brandAltName,
        delay: 5,
        duration: 3,
        no_of_conversions: 10,
        position: 'top left',
        title_color: '#000000',
        description_color: '#636363',
        background_color: '#FFFFFF',
        show_timestamps: false,
        show_real_names: false,
        location_format: 1,
        avatar_display: 1,
        show_on_mobile: false
      },
      position: 'top left',
      widgetsTabs: [
        { title: this.$t('frontend_info'), value: 'info' },
        { title: this.$t('frontend_display'), value: 'display' },
        { title: this.$t('frontend_design'), value: 'design' }
      ],
      widgetsTab: 'info',
      widgetsTableColumns: ['name', 'actions'],
      widgetsTableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc'
        },
        sortable: [],
        headings: {
          name: this.$t('frontend_name'),
          actions: this.$t('frontend_actions'),
          title: 'Name',
          expand: ''
        },
        filterByColumn: false,
        filterable: false,
        childRowTogglerFirst: false,
        perPage: 10000,
        rowClassCallback() {
          return 'hover:bg-grey-50 funnelRow';
        }
      }
    };
  },
  validations: {
    localModel: {},
    proofModel: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(80)
      },
      notification_text: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(80)
      }
    }
  },
  computed: {
    proofOptions() {
      return [
        { value: 5, text: `5 ${this.$t('frontend_seconds')}` },
        { value: 10, text: `10 ${this.$t('frontend_seconds')}` },
        { value: 15, text: `15 ${this.$t('frontend_seconds')}` },
        { value: 20, text: `20 ${this.$t('frontend_seconds')}` },
        { value: 60, text: `60 ${this.$t('frontend_seconds')}` },
      ];
    },
    proofDurationOptions() {
      return [
        { value: 3, text: `3 ${this.$t('frontend_seconds')}` },
        { value: 5, text: `5 ${this.$t('frontend_seconds')}` },
        { value: 10, text: `10 ${this.$t('frontend_seconds')}` }
      ];
    },
    proofNoOfNotificationsOptions() {
      return [5, 10, 25, 50, 100].map(item => {
        return { value: item, text: item };
      });
    },
    positionOptions() {
      return [
        { value: 'top left', text: this.$t('frontend_top_left') },
        { value: 'top right', text: this.$t('frontend_top_right') },
        { value: 'bottom left', text: this.$t('frontend_bottom_left') },
        { value: 'bottom right', text: this.$t('frontend_bottom_right') }
      ];
    },
    locationFormatOptions() {
      return [
        { value: 1, text: this.$t('frontend_product_step10_location_format_option1') },
        { value: 2, text: this.$t('frontend_product_step10_location_format_option2') },
        { value: 3, text: this.$t('frontend_product_step10_location_format_option3') },
        { value: 4, text: this.$t('frontend_product_step10_location_format_option4') }
      ];
    },
    avatarDisplayOptions() {
      return [
        { value: 1, text: this.$t('frontend_product_step10_avatar_display_option1') },
        { value: 2, text: this.$t('frontend_product_step10_avatar_display_option2') },
        { value: 3, text: this.$t('frontend_product_step10_avatar_display_option3') }
      ];
    },
  },
  async created() {
    this.$loader.start('fetchProofWidgets');
    this.proofWidgets = await this.product.proof_widgets().$get();
    this.proofModel = {
      product_id: this.$route.params.product_id || this.product.id,
      name: '',
      notification_text: this.$t('frontend_product_step10_notification_text', {brandAltName: this.$store.state.brandData.brandAltName}),
      delay: 5,
      duration: 3,
      no_of_conversions: 10,
      position: 'top left',
      title_color: '#000000',
      description_color: '#636363',
      background_color: '#FFFFFF',
      show_timestamps: false,
      show_real_names: false,
      location_format: 1,
      avatar_display: 1,
      show_on_mobile: false
    };
    this.$loader.end('fetchProofWidgets');
  },
  methods: {
    handleWidgetsTabChange(newTab) {
      this.widgetsTab = newTab;
    },

    validate() {
      this.$v.localModel.$touch();
      const isValid = !this.$v.localModel.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },

    async createWidget() {
      this.proofWidget = new ProofWidget({});

      this.proofModel = {
        product_id: this.$route.params.product_id || this.product.id,
        name: '',
        notification_text: this.$t('frontend_product_step10_notification_text', {brandAltName: this.$store.state.brandData.brandAltName}),
        delay: 5,
        duration: 3,
        no_of_conversions: 10,
        position: 'bottom left',
        title_color: '#000000',
        description_color: '#636363',
        background_color: '#FFFFFF',
        show_timestamps: false,
        show_real_names: false,
        location_format: 1,
        avatar_display: 1,
        show_on_mobile: false
      };

      this.$v.proofModel.$reset();
      this.widgetsTab = 'info';
      this.isEditing = false;
      this.showWidgetEditor = true;
    },

    async saveWidget() {
      this.$v.proofModel.$touch();

      if (!this.$v.proofModel.$invalid) {
        this.$loader.start('saveWidget');
        this.proofWidget.product_id = this.proofModel.product_id;
        this.proofWidget.name = this.proofModel.name;
        this.proofWidget.notification_text = this.proofModel.notification_text;
        this.proofWidget.delay = this.proofModel.delay;
        this.proofWidget.duration = this.proofModel.duration;
        this.proofWidget.no_of_conversions = this.proofModel.no_of_conversions;
        this.proofWidget.position = this.proofModel.position;
        this.proofWidget.title_color = this.proofModel.title_color;
        this.proofWidget.description_color = this.proofModel.description_color;
        this.proofWidget.background_color = this.proofModel.background_color;
        this.proofWidget.show_timestamps = this.proofModel.show_timestamps;
        this.proofWidget.show_real_names = this.proofModel.show_real_names;
        this.proofWidget.location_format = this.proofModel.location_format;
        this.proofWidget.avatar_display = this.proofModel.avatar_display;
        this.proofWidget.show_on_mobile = this.proofModel.show_on_mobile;
        await this.proofWidget.save();
        this.showWidgetEditor = false;
        this.proofWidgets = await this.product.proof_widgets().$get();
        this.$loader.end('saveWidget');
      }
    },

    async openEditWidget(id) {
      this.$loader.start('openEditWidget');
      this.$v.proofModel.$reset();
      this.proofWidget = await ProofWidget.$find(id);
      this.proofModel = {
        name: this.proofWidget.name,
        notification_text: this.proofWidget.notification_text,
        delay: this.proofWidget.delay,
        duration: this.proofWidget.duration,
        no_of_conversions: this.proofWidget.no_of_conversions || 10,
        position: this.proofWidget.position,
        title_color: this.proofWidget.title_color,
        description_color: this.proofWidget.description_color,
        background_color: this.proofWidget.background_color,
        show_timestamps: this.proofWidget.show_timestamps,
        show_real_names: this.proofWidget.show_real_names,
        location_format: this.proofWidget.location_format,
        avatar_display: this.proofWidget.avatar_display,
        show_on_mobile: this.proofWidget.show_on_mobile
      };

      this.widgetsTab = 'info';
      this.isEditing = true;
      this.showWidgetEditor = true;
      this.$loader.end('openEditWidget');
    },
    closeWidgetEditor() {
      this.showWidgetEditor = false;
    },
    openConfirmModal(id) {
      this.widgetId = id;
      this.showDeleteWidgetConfirm = true;
    },
    async deleteWidget() {
        this.$loader.start('deleteWidget');
        const Proof_Widget = await ProofWidget.$find(this.widgetId);
        await Proof_Widget.delete();
        const deleted = this.proofWidgets.map(item => item.id).indexOf(this.widgetId);
        this.proofWidgets.splice(deleted, 1);
        this.$loader.end('deleteWidget');
    },
  }
};
</script>
<style>

.gs-proof-step__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.gs-proof-step__head h2 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}


.gs-proof-step__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1fr auto;
  align-items: center;
  min-height: 52px;
  margin-top: 8px;
  padding: 0 14px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
}

.gs-proof-step__row strong {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-proof-step__row span {
  color: var(--gs-muted-foreground);
  font-size: 14px;
}

.gs-proof-step__row div {
  display: flex;
  gap: 8px;
}

.gs-proof-step__empty {
  padding: 20px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  text-align: center;
  border: 1px dashed var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-proof-step__drawer {
  width: 100%;
  min-height: 100%;
  overflow: auto;
  background: #ffffff;
  border-radius: 0;
  box-shadow: none;
}

.gs-proof-step__drawer-body {
  padding: 0;
}

.gs-proof-step__drawer-body .flex.flex-row.w-full {
  gap: 16px;
}

.gs-proof-step__drawer-body .w-1\/2,
.gs-proof-step__drawer-body .w-1\/3 {
  min-width: 0;
}

@media (max-width: 720px) {
  .gs-proof-step__drawer {
    width: 100vw;
    border-radius: 0;
  }

  .gs-proof-step__drawer-body .flex.flex-row.w-full {
    flex-direction: column;
  }

  .gs-proof-step__drawer-body .w-1\/2,
  .gs-proof-step__drawer-body .w-1\/3 {
    width: 100%;
  }
}

/* vue-color chrome picker trim (third-party widget markup). */
.vue-color__chrome__alpha-wrap,
.vue-color__chrome__toggle-btn,
.vue-color__chrome__fields-wrap,
.vue-color__chrome__color-wrap {
  display: none !important;
}

.vue-color__chrome__controls {
  align-items: center;
}

.vue-color__chrome__active-color {
  margin-top: 0;
}
</style>
